/* @ts-ignore */
import queryStringHandler from "query-string-handler";
import { scrollToElement } from "@/components/global/scroll-to-element";

interface ResultItem {
  image?: ResultItemImage;
  imageFit?: string;
  contentTypeLabel?: string;
  title?: string;
  description?: string;
  url?: string;
}

interface ResultItemImage {
  url?: string | undefined;
  altText?: string;
}

export default function CustomerSuccessIndexPage() {
  const customerSuccessIndex = document.querySelector<HTMLDivElement>(".js-customer-success-index");
  const filtersMenu = document.querySelector<HTMLDivElement>(".js-filters-mobile-menu");
  const fixedMenuWrapper = document.querySelector<HTMLDivElement>(".js-fixed-menu");

  if (customerSuccessIndex) {
    const selectFilters = customerSuccessIndex.querySelectorAll<HTMLLIElement>(".js-filter-item");
    const selectFilterBtns = customerSuccessIndex.querySelectorAll<HTMLButtonElement>(".js-filter-btn");

    const prevPageBtn = customerSuccessIndex.querySelector<HTMLButtonElement>(".js-prev-btn");
    const nextPageBtn = customerSuccessIndex.querySelector<HTMLButtonElement>(".js-next-btn");

    const clearBtns = customerSuccessIndex.querySelectorAll<HTMLButtonElement>(".js-clear-btn");
    const openBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-open-menu-btn");
    const cancelBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-cancel-btn");
    const applyBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-apply-btn");

    const mobileFilterItems = filtersMenu?.querySelectorAll<HTMLDivElement>(".js-mobile-filter-item");
    const mobileFilterBtns = filtersMenu?.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");

    const resultsWrapper = customerSuccessIndex.querySelector<HTMLDivElement>(".js-results-wrapper");
    const resultsList = customerSuccessIndex.querySelector<HTMLDivElement>(".js-results-list");

    let page: number = 1;
    let totalPages: number = 0;
    let savedPage = 0;

    let companySize: string = "";
    let savedCompanySize: string = "";

    let product: string = "";
    let savedProduct: string = "";

    let industry: string = "";
    let savedIndustry: string = "";

    let results: ResultItem[] = [];

    let disabledFacets: string[] = [];

    // Results
    const handleFetchResults = async (pg: number, cs: string, pd: string, ind: string) => {
      try {
        const url = `/api/search/customer-success-stories?pg=${pg}&cs=${cs}&pd=${pd}&ind=${ind}`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        // clear results array
        results = [];
        handleRemoveCards();

        if (data.items && data.items.length > 0) {
          results = data.items;
          customerSuccessIndex.setAttribute("data-noresults", "false");
        } else {
          customerSuccessIndex.setAttribute("data-noresults", "true");
        }

        if (data.facetsCollections && data.facetsCollections.length > 0) {
          handleDisabledFacetBtns();
          handleUpdateSelectValue("cs", cs);
          handleUpdateSelectValue("pd", pd);
          handleUpdateSelectValue("ind", ind);
          handleUpdateClearBtn();
        }

        totalPages = data.totalPages;

        handleNewCards();
        handleDisabledPagination();
        customerSuccessIndex.setAttribute("data-loading", "false");
      } catch (error) {
        console.error("Error during GET request:", error);
        customerSuccessIndex.setAttribute("data-noresults", "true");
        customerSuccessIndex.setAttribute("data-loading", "false");
      }
    };

    const handleNewCards = () => {
      if (results.length > 0) {
        customerSuccessIndex.setAttribute("data-noresults", "false");
        resultsWrapper?.setAttribute("data-results", "true");

        results.forEach((item: ResultItem) => {
          resultsList?.insertAdjacentHTML("beforeend", handleCreateCustomerSuccessCard(item));
        });
      } else {
        customerSuccessIndex.setAttribute("data-noresults", "true");
      }
    };

    const handleCreateCustomerSuccessCard = (item: ResultItem) => {
      let imageFitClasses = "object-cover";
      if (item.imageFit !== undefined && item.imageFit !== "cover") {
        imageFitClasses = "object-contain";
      }

      return `
                <article class="js-result-item flex flex-col">
                    <a class="flex h-full hover:no-underline focus:no-underline" href="${item.url}" arial-label="${item.title}">
                        <div class="w-full flex flex-col rounded-3xl border border-neutral-100 p-5 gap-5">
                            ${
                              item?.image?.url
                                ? `
                                        <div class="flex justify-center items-center">
                                          <div class="relative aspect-video w-full">
                                            <img class="absolute inset-0 h-full w-full ${imageFitClasses} rounded-xl" src="${item.image.url}" alt="${item.image.altText}" height="162" />
                                          </div>
                                        </div>
                                    `
                                : ""
                            }
                            ${
                              item?.title
                                ? `
                                        <div class="text-[14px] font-medium leading-[21px] tracking-[0.7px] uppercase text-primary-600">${item.title}</div>
                                    `
                                : ""
                            }
                            ${
                              item?.description
                                ? `
                                        <div class="text-primary-600 leading-[27px] line-clamp-5">${item.description}</div>
                                    `
                                : ""
                            }
                        </div>
                    </a>
                </article>
            `;
    };

    const handleRemoveCards = () => {
      customerSuccessIndex.setAttribute("data-results", "false");
      const resultItems = resultsList?.querySelectorAll<HTMLDivElement>(".js-result-item");

      if (resultItems && resultItems.length > 0) {
        resultItems.forEach((item) => item.remove());
      }
    };

    const handleUpdateQuery = (query: string, value: string) => {
      queryStringHandler.replace({ [query]: value });

      if (query === "cs") {
        companySize = value;
      }

      if (query === "pd") {
        product = value;
      }

      if (query === "ind") {
        industry = value;
      }
      // reset to first page on change
      if (page !== 1 && (query === "cs" || query === "pd" || query === "ind")) {
        page = 1;
        queryStringHandler.replace({ pg: "1" });
      }

      handleFetchResults(page, companySize, product, industry);
    };

    // Paging
    const handlePreviousPage = async () => {
      page--;
      handleUpdateQuery("pg", page.toString());
      await handleFetchResults(page, companySize, product, industry);
      handleScrollToTop();
    };

    const handleNextPage = async () => {
      page++;
      handleUpdateQuery("pg", page.toString());
      await handleFetchResults(page, companySize, product, industry);
      handleScrollToTop();
    };

    const handleDisabledPagination = () => {
      if (prevPageBtn) {
        if (page === 1) {
          prevPageBtn.disabled = true;
        } else {
          prevPageBtn.disabled = false;
        }
      }

      if (nextPageBtn) {
        if (page === totalPages) {
          nextPageBtn.disabled = true;
        } else {
          nextPageBtn.disabled = false;
        }
      }
    };

    // Filter Menus
    const handleUpdateSelectValue = (selectedQuery: string, value: string) => {
      selectFilters.forEach((item) => {
        const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-text");
        const groupQuery = item.getAttribute("data-query");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");

        if (filterBtns && filterBtns.length > 0 && menuBtnSelectedText && groupQuery === selectedQuery.trim()) {
          let selectedIndex = 0;

          filterBtns.forEach((btn, index) => {
            if (btn.getAttribute("data-value") === value) {
              menuBtnSelectedText.innerText = btn.textContent || "";
              selectedIndex = index;
            }
          });

          filterBtns[selectedIndex].disabled = true;
        }
      });

      mobileFilterItems?.forEach((item) => {
        const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
        const groupQuery = item.getAttribute("data-group-query");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");

        if (menuBtnSelectedText && filterBtns && filterBtns.length > 0 && groupQuery === selectedQuery.trim()) {
          let selectedIndex = 0;
          filterBtns.forEach((btn, index) => {
            if (btn.getAttribute("data-value") === value) {
              menuBtnSelectedText.innerText = btn.textContent || "";
              selectedIndex = index;
            }
          });

          filterBtns[selectedIndex].disabled = true;
        }
      });
    };

    const handleDisabledFacetBtns = () => {
      if (selectFilterBtns && selectFilterBtns.length > 0) {
        selectFilterBtns.forEach((btn) => {
          const text = btn.innerText;
          if (disabledFacets.includes(text.trim())) {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
        });
      }

      if (mobileFilterBtns && mobileFilterBtns.length > 0) {
        mobileFilterBtns.forEach((btn) => {
          const text = btn.innerText;
          if (disabledFacets.includes(text.trim())) {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
        });
      }
    };

    const handleClearFilters = () => {
      companySize = "";
      product = "";
      industry = "";
      page = 1;
      queryStringHandler.replace({ cs: "" });
      queryStringHandler.replace({ pd: "" });
      queryStringHandler.replace({ ind: "" });
      queryStringHandler.replace({ pg: "1" });

      handleFetchResults(page, companySize, product, industry);
    };

    // Mobile Filter Menus
    const handleOpenMenu = () => {
      if (filtersMenu && fixedMenuWrapper) {
        filtersMenu.setAttribute("data-open", "true");
        document.body.style.overflow = "hidden";
        fixedMenuWrapper.style.height = `${window.innerHeight}px`;
        handleSetSavedData();
      }
    };

    const handleCloseMenu = () => {
      if (filtersMenu && fixedMenuWrapper) {
        filtersMenu.setAttribute("data-open", "false");
        fixedMenuWrapper.style.height = "";
        document.body.style.overflow = "";
      }
    };

    const handleApplyChanges = () => {
      handleCloseMenu();
    };

    const handleCancelChanges = () => {
      handleCloseMenu();
      if (savedCompanySize !== companySize || savedProduct !== product || savedIndustry !== industry || savedPage !== page) {
        queryStringHandler.replace({ cs: savedCompanySize });
        queryStringHandler.replace({ pd: savedProduct });
        queryStringHandler.replace({ ind: savedIndustry });
        queryStringHandler.replace({ pg: savedPage });
        page = savedPage;

        handleFetchResults(savedPage, savedCompanySize, savedProduct, savedIndustry);
      }
    };

    const handleUpdateClearBtn = () => {
      const companySizeQuery: string = queryStringHandler.getValue("cs");
      const productQuery: string = queryStringHandler.getValue("pd");
      const industryQuery: string = queryStringHandler.getValue("ind");
      let showClearBtn: string = "false";

      if (companySizeQuery || productQuery || industryQuery) {
        showClearBtn = "true";
      }

      clearBtns.forEach((btn) => {
        btn.setAttribute("data-show", showClearBtn);
      });
    };

    const handleSetSavedData = () => {
      savedCompanySize = companySize;
      savedProduct = product;
      savedIndustry = industry;
    };

    // Misc
    const handleResize = () => {
      if (filtersMenu && filtersMenu.getAttribute("data-open") === "true") {
        handleCloseMenu();
      }
    };

    const handleScrollToTop = () => {
      scrollToElement(customerSuccessIndex);
    };

    // Initialize
    mobileFilterItems?.forEach((item) => {
      const selectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
      const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");
      const accordionBtn = item.querySelector<HTMLButtonElement>(".js-accordion-btn");
      const accordionContentWrapper = item.querySelector<HTMLDivElement>(".js-accordion-content-wrapper");

      const handleCloseAccordion = () => {
        if (accordionBtn && accordionContentWrapper) {
          accordionBtn.setAttribute("aria-expanded", "false");
          accordionContentWrapper.style.maxHeight = "";
        }
      };

      filterBtns.forEach((btn) => {
        const groupQuery = btn.getAttribute("data-query");
        const queryValue = btn.getAttribute("data-value");

        btn.addEventListener("click", () => {
          if (selectedText) {
            selectedText.innerText = btn.textContent || "";
          }
          if (groupQuery) {
            handleUpdateQuery(groupQuery, queryValue || "");
            handleCloseAccordion();
          }
        });
      });
    });

    if (prevPageBtn) {
      prevPageBtn.addEventListener("click", handlePreviousPage);
    }

    if (nextPageBtn) {
      nextPageBtn.addEventListener("click", handleNextPage);
    }

    if (clearBtns && clearBtns.length > 0) {
      clearBtns.forEach((btn) => {
        btn.addEventListener("click", handleClearFilters);
      });
    }

    if (selectFilters) {
      selectFilters.forEach((item) => {
        const menuBtn = item.querySelector<HTMLSelectElement>(".js-select-menu-btn");
        const menuBtnSelectedText = menuBtn?.querySelector<HTMLSpanElement>(".js-selected-text");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
        const groupQuery = item.getAttribute("data-query");

        const handleTabIndex = () => {
          filterBtns.forEach((btn) => {
            if (item.getAttribute("data-open") === "true") {
              btn.tabIndex = 0;
            } else {
              btn.tabIndex = -1;
            }
          });
        };

        const handleToggleMenu = () => {
          if (item.getAttribute("data-open") === "false") {
            item.setAttribute("data-open", "true");
            handleTabIndex();
          } else {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        };

        const handleUpdateSelectedItem = (value: string) => {
          if (menuBtnSelectedText) {
            menuBtnSelectedText.innerText = value;
          }
        };

        filterBtns.forEach((btn, i) => {
          const value = btn.getAttribute("data-value");
          const text = btn.textContent;

          if (groupQuery && text) {
            btn.addEventListener("click", () => {
              handleUpdateSelectedItem(text);
              handleToggleMenu();
              handleUpdateQuery(groupQuery, value || "");
            });

            btn.addEventListener("keydown", (e: KeyboardEvent) => {
              if (e.code === "Escape") {
                item.setAttribute("data-open", "false");
                menuBtn?.focus();
                handleTabIndex();
              }
            });
          }

          if (i === filterBtns.length - 1) {
            btn.addEventListener("keydown", (e: KeyboardEvent) => {
              if (e.code === "Tab" && !e.shiftKey) {
                item.setAttribute("data-open", "false");
                handleTabIndex();
              }
            });
          }
        });

        if (menuBtn) {
          menuBtn.addEventListener("click", handleToggleMenu);
        }

        document.addEventListener("click", (e: MouseEvent) => {
          const target = e.target as Node;
          if (!item.contains(target) && item.getAttribute("data-open") === "true") {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        });
      });
    }

    // Initialize Mobile Menus
    if (openBtn) {
      openBtn.addEventListener("click", handleOpenMenu);
    }

    if (cancelBtn) {
      cancelBtn.addEventListener("click", handleCancelChanges);
    }

    if (applyBtn) {
      applyBtn.addEventListener("click", handleApplyChanges);
    }

    document.addEventListener("resize", handleResize);

    // set values if they're in the query string on load
    document.addEventListener("DOMContentLoaded", () => {
      const pageQuery: number = queryStringHandler.getValue("pg") ? parseInt(queryStringHandler.getValue("pg")) : page;
      const companySizeQuery: string = queryStringHandler.getValue("cs") || companySize;
      const productQuery: string = queryStringHandler.getValue("pd") || product;
      const industryQuery: string = queryStringHandler.getValue("ind") || industry;

      // on fetch for results if there is a query param that has filters
      if (pageQuery !== page || companySizeQuery !== companySize || productQuery !== product || industryQuery !== industry) {
        if (pageQuery !== page) {
          page = pageQuery;
        }

        if (companySizeQuery !== companySize) {
          companySize = companySizeQuery;
        }

        if (productQuery !== product) {
          product = productQuery;
        }

        if (industryQuery !== industry) {
          industry = industryQuery;
        }

        handleFetchResults(page, companySize, product, industry);
      } else {
        handleDisabledPagination();
        customerSuccessIndex.setAttribute("data-loading", "false");
      }
    });
  }
}
